// Methodenkatalog V1 — 8 Templates, abgeleitet aus
// `ms-whiteboard_RequirementsWithArchitecture.md` §4.5 FR-018 + §3 UC-01..03.
//
// In S3.3 nur Namen + UC-Zuordnung fuer Starter Picker.
// In Phase 5 (S5.x) kommen Region-/Block-Scaffolds dazu.

export type UseCaseId = "uc-01-ist" | "uc-02-anforderungen" | "uc-03-loesungsbild";

export interface UseCase {
  readonly id: UseCaseId;
  readonly shortCode: string;
  readonly nameKey: string;
  readonly descriptionKey: string;
}

export const USE_CASES: readonly UseCase[] = [
  {
    id: "uc-01-ist",
    shortCode: "UC-01",
    nameKey: "uc.uc01.name",
    descriptionKey: "uc.uc01.description",
  },
  {
    id: "uc-02-anforderungen",
    shortCode: "UC-02",
    nameKey: "uc.uc02.name",
    descriptionKey: "uc.uc02.description",
  },
  {
    id: "uc-03-loesungsbild",
    shortCode: "UC-03",
    nameKey: "uc.uc03.name",
    descriptionKey: "uc.uc03.description",
  },
];

export type MethodId =
  | "process-high-level"
  | "process-swimlane"
  | "sipoc"
  | "pain-dependency-cluster"
  | "requirements-cluster"
  | "moscow"
  | "impact-effort"
  | "context-target-light";

export interface MethodTemplateDef {
  readonly id: MethodId;
  readonly nameKey: string;
  readonly descriptionKey: string;
  /** UCs in denen die Methode primaer angeboten wird. */
  readonly useCases: readonly UseCaseId[];
  /** Layout-Familie, fuer S5-Scaffold. */
  readonly family: "process" | "cluster" | "matrix" | "diagram";
  /**
   * i18n-Keys fuer die Regions in der Scaffold-Reihenfolge.
   * Index korrespondiert zu `MethodLayout.regions[i]`.
   * Block-Label wird ueber `nameKey` aufgeloest.
   */
  readonly regionLabelKeys: readonly string[];
}

export const METHOD_CATALOG: readonly MethodTemplateDef[] = [
  {
    id: "process-high-level",
    nameKey: "method.processHighLevel.name",
    descriptionKey: "method.processHighLevel.description",
    useCases: ["uc-01-ist"],
    family: "process",
    regionLabelKeys: [
      "method.region.process.start",
      "method.region.process.step1",
      "method.region.process.step2",
      "method.region.process.step3",
      "method.region.process.end",
    ],
  },
  {
    id: "process-swimlane",
    nameKey: "method.processSwimlane.name",
    descriptionKey: "method.processSwimlane.description",
    useCases: ["uc-01-ist"],
    family: "process",
    regionLabelKeys: [
      "method.region.swimlane.lane1",
      "method.region.swimlane.lane2",
      "method.region.swimlane.lane3",
    ],
  },
  {
    id: "sipoc",
    nameKey: "method.sipoc.name",
    descriptionKey: "method.sipoc.description",
    useCases: ["uc-01-ist"],
    family: "process",
    regionLabelKeys: [
      "method.region.sipoc.supplier",
      "method.region.sipoc.input",
      "method.region.sipoc.process",
      "method.region.sipoc.output",
      "method.region.sipoc.customer",
    ],
  },
  {
    id: "pain-dependency-cluster",
    nameKey: "method.painDependency.name",
    descriptionKey: "method.painDependency.description",
    useCases: ["uc-01-ist", "uc-02-anforderungen"],
    family: "cluster",
    regionLabelKeys: [
      "method.region.painDependency.painHigh",
      "method.region.painDependency.painMed",
      "method.region.painDependency.painLow",
      "method.region.painDependency.depHigh",
      "method.region.painDependency.depMed",
      "method.region.painDependency.depLow",
    ],
  },
  {
    id: "requirements-cluster",
    nameKey: "method.requirementsCluster.name",
    descriptionKey: "method.requirementsCluster.description",
    useCases: ["uc-02-anforderungen"],
    family: "cluster",
    regionLabelKeys: [
      "method.region.reqCluster.functional",
      "method.region.reqCluster.nonFunctional",
      "method.region.reqCluster.data",
      "method.region.reqCluster.outOfScope",
    ],
  },
  {
    id: "moscow",
    nameKey: "method.moscow.name",
    descriptionKey: "method.moscow.description",
    useCases: ["uc-02-anforderungen"],
    family: "matrix",
    regionLabelKeys: [
      "method.region.moscow.must",
      "method.region.moscow.should",
      "method.region.moscow.could",
      "method.region.moscow.wont",
    ],
  },
  {
    id: "impact-effort",
    nameKey: "method.impactEffort.name",
    descriptionKey: "method.impactEffort.description",
    useCases: ["uc-02-anforderungen", "uc-03-loesungsbild"],
    family: "matrix",
    regionLabelKeys: [
      "method.region.impactEffort.quickWins",
      "method.region.impactEffort.majorProjects",
      "method.region.impactEffort.fillIns",
      "method.region.impactEffort.thankless",
    ],
  },
  {
    id: "context-target-light",
    nameKey: "method.contextTarget.name",
    descriptionKey: "method.contextTarget.description",
    useCases: ["uc-03-loesungsbild"],
    family: "diagram",
    regionLabelKeys: [
      "method.region.context.system",
      "method.region.context.actors",
      "method.region.context.systems",
      "method.region.context.data",
      "method.region.context.interfaces",
    ],
  },
];

export function methodsForUseCase(uc: UseCaseId): readonly MethodTemplateDef[] {
  return METHOD_CATALOG.filter((m) => m.useCases.includes(uc));
}

/** Familien, deren Objekte frei platziert werden (Drop-Position bleibt erhalten, kein
 *  Grid-Snap) — Prozesse + Diagramme brauchen Layout-Freiraum fuer Pfeile/Ausgestaltung.
 *  cluster/matrix snappen weiter ins Grid (dort gewollt). */
const FREE_POSITION_FAMILIES: ReadonlySet<MethodTemplateDef["family"]> = new Set([
  "process",
  "diagram",
]);

export function methodPlacesFreely(methodId: string): boolean {
  const def = METHOD_CATALOG.find((m) => m.id === methodId);
  return def ? FREE_POSITION_FAMILIES.has(def.family) : false;
}
